import { isBoolean } from 'lodash';
import { useMemo, useState } from 'react';

import ButtonsTagsPositions from './ButtonsTagsPositions';

import { useTeamStore } from '@/hooks/useTeamStore';
import { TeamActionTypes } from '@/store/team';
import { Maybe, KeysOf, ChangeTacticAction, PlayerPositionAbbr } from '@/types';
import {
  getPositionLabelByPositionId,
  PLAYER_POSITION_BY_ID,
  POSITIONS_LABELS,
} from '@/utils';

type PositionKey = KeysOf<typeof PLAYER_POSITION_BY_ID>;

type Props = {
  onSubmit?: (pos: Maybe<PlayerPositionAbbr>) => void;
  positionByFilter?: Maybe<PlayerPositionAbbr | PlayerPositionAbbr[]>;
};

export const ButtonsTagsPositionsF11 = ({
  positionByFilter,
  onSubmit,
}: Props) => {
  const { state, dispatch } = useTeamStore();
  const [stateComponent, setStateComponent] =
    useState<Maybe<PlayerPositionAbbr>>(null);

  const isDisabledByStepChangeTactic = (positionId: number) => {
    if (state.step) {
      const isAddAction = state.step.action === ChangeTacticAction.ADD;
      const position = PLAYER_POSITION_BY_ID[state.step.position];
      const positionLabel = getPositionLabelByPositionId(positionId);

      return !(isAddAction && position === positionLabel);
    }

    return null;
  };

  const selectedPlayerPositionOnField = useMemo(() => {
    if (positionByFilter) {
      return positionByFilter;
    }

    if (state.filtersApplied?.position) {
      return state.filtersApplied?.position;
    }

    if (
      state.selectedPosition === stateComponent &&
      state.showFiltersPlayersList
    ) {
      return null;
    }

    if (state.selectedPosition) {
      return state.selectedPosition;
    }

    return null;
  }, [
    state.selectedPosition,
    state.showFiltersPlayersList,
    stateComponent,
    positionByFilter,
    state.filtersApplied?.position,
  ]);

  const isDisabledPositionButton = (positionId: PositionKey) => {
    const isDisabledByChangeTactic = isDisabledByStepChangeTactic(positionId);

    if (isBoolean(isDisabledByChangeTactic)) {
      return isDisabledByChangeTactic;
    }

    if (state.selectedPlayerOnTeam) {
      return false;
    }

    if (
      state.selectedPlayerOnBuildTeam ||
      (state.showFiltersPlayersList && state.selectedPosition)
    ) {
      const positionLabel = getPositionLabelByPositionId(positionId);
      return Boolean(selectedPlayerPositionOnField !== positionLabel);
    }
  };

  const handleSelectPositionButton = (positionId: PositionKey) => {
    const isDisabledByChangeTactic = isDisabledByStepChangeTactic(positionId);

    if (state.selectedPlayerOnBuildTeam || isDisabledByChangeTactic === false) {
      return;
    }

    const positionLabel = PLAYER_POSITION_BY_ID[positionId];

    const position =
      selectedPlayerPositionOnField === positionLabel ? null : positionLabel;

    if (!state.showFiltersPlayersList) {
      dispatch({
        type: TeamActionTypes.SET_STATE,
        payload: {
          selectedPosition: position,
        },
      });
    } else {
      const selectedPosition =
        state.selectedPosition &&
        state.selectedPosition !== POSITIONS_LABELS.all_field_players
          ? state.selectedPosition
          : null;

      setStateComponent(selectedPosition);
      onSubmit && onSubmit(position);
    }
  };

  const handleIsChecked = (id: PositionKey): boolean => {
    return selectedPlayerPositionOnField === PLAYER_POSITION_BY_ID[id];
  };

  return (
    <ButtonsTagsPositions
      handleIsChecked={handleIsChecked}
      handleSelectPositionButton={handleSelectPositionButton}
      isDisabledPositionButton={isDisabledPositionButton}
    />
  );
};
